import { useEffect } from "react"
import { Routes, Route } from "react-router-dom"
import { Toaster, ToastBar, toast } from "react-hot-toast"

import { ROUTES } from "./constants"
import { useScrollToTop } from "./hooks"
import { useAuthStore } from "./stores/useAuthStore"
import { Spinner } from "./components/ui"

// Layout
import { Main } from "./pages/Main/Main"
import { ModalBase } from "./components/modals/ModalBase/ModalBase"

// Public pages
import { NotFound } from "./pages/NotFound/NotFound"

// Protected pages
import { Profile } from "./pages/Profile/Profile"
import { ProtectedRoute } from "./components/features/ProtectedRoute/ProtectedRoute"
import { WorkerGuard } from "./components/worker/WorkerGuard/WorkerGuard"
import { AdminGuard } from "./components/admin/AdminGuard/AdminGuard"

// Auth pages
import { Login } from "./pages/auth/Login/Login"
import { Register } from "./pages/auth/Register/Register"
import { VerifyEmail } from "./pages/auth/VerifyEmail/VerifyEmail"
import { ForgotPassword } from "./pages/auth/ForgotPassword/ForgotPassword"
import { ChangePassword } from "./pages/auth/ChangePassword/ChangePassword"

// Worker pages
import { OnboardingPage } from "./pages/worker/OnboardingPage/OnboardingPage"
import { FeedPage } from "./pages/worker/FeedPage/FeedPage"
import { JobDetailPage } from "./pages/worker/JobDetailPage/JobDetailPage"
import { AssignmentsPage } from "./pages/worker/AssignmentsPage/AssignmentsPage"
import { AssignmentDetailPage } from "./pages/worker/AssignmentDetailPage/AssignmentDetailPage"

// Admin pages
import { AdminWorkersPage } from "./pages/admin/AdminWorkersPage/AdminWorkersPage"

// Docs
import { ApiDocs } from "./pages/ApiDocs/ApiDocs"

import './App.css'

function App() {
    useScrollToTop();
    const isLoading = useAuthStore((s) => s.isLoading);

    useEffect(() => {
        useAuthStore.getState().bootstrap();
    }, []);

    if (isLoading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <Spinner size="lg" />
        </div>
    );

    return (
        <div className="App">
            <ModalBase />
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 5000,
                    success: { className: "toast-success" },
                    error: { className: "toast-error" },
                    style: {
                        zIndex: 20
                    }
                }}
                children={(toasts) => (
                    <ToastBar toast={toasts}>
                        {({ icon, message }) => (
                            <div
                              onClick={() => toast.dismiss()}
                              style={{ cursor: "pointer", display: "flex"}}
                            >
                                {icon}
                                {message}
                            </div>
                        )}
                    </ToastBar>
                )}
            />
            <Routes>
                {/* Public auth endpoints */}
                <Route path={ROUTES.login} element={<Login />} />
                <Route path={ROUTES.register} element={<Register />} />
                <Route path={ROUTES.verifyEmail} element={<VerifyEmail />} />
                <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />

                {/* Protected auth endpoints */}
                <Route element={<ProtectedRoute redirectToLogin />}>
                    <Route path={ROUTES.changePassword} element={<ChangePassword />} />
                </Route>

                <Route path={ROUTES.main} element={<Main />}>
                    <Route element={<ProtectedRoute redirectToLogin />}>
                        <Route path={ROUTES.profile} element={<Profile />} />
                        <Route path={ROUTES.onboarding} element={<OnboardingPage />} />

                        {/* Worker routes - require approved worker profile */}
                        <Route element={<WorkerGuard />}>
                            <Route path={ROUTES.home} element={<FeedPage />} />
                            <Route path={ROUTES.jobDetail} element={<JobDetailPage />} />
                            <Route path={ROUTES.assignments} element={<AssignmentsPage />} />
                            <Route path={ROUTES.assignmentDetail} element={<AssignmentDetailPage />} />
                        </Route>

                        {/* Admin routes - require is_staff */}
                        <Route element={<AdminGuard />}>
                            <Route path={ROUTES.admin} element={<AdminWorkersPage />} />
                            <Route path={ROUTES.adminWorkers} element={<AdminWorkersPage />} />
                        </Route>
                    </Route>
                </Route>
                <Route path={ROUTES.apiDocs} element={<ApiDocs />} />
                <Route path={ROUTES.notFound} element={<NotFound />} />
            </Routes>
        </div>
    )
}

export { App }
